import { useEffect, useState, type SyntheticEvent } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { BottomCTA, IconPersonFill, MarkdownBody, StaticChip, TopNavigation } from "@hamlak/ui";
import { FeedbackEntry } from "@/components/FeedbackEntry";
import { analytics } from "@/analytics";
import { t } from "@/i18n/ko";
import { EmptyState } from "@/components/EmptyState";
import { onboardingStep } from "@/app/onboarding";
import { publicWebUrl } from "@/config/publicUrl";
import { ReportScreen } from "@/features/report/ReportScreen";
import { platform } from "@/platform";
import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";
import { PlotMenuSheet } from "./PlotMenuSheet";
import { BlockRenderer } from "./blocks/BlockRenderer";
import { CharacterCarousel } from "./CharacterCarousel";
import { useCanonicalPlotPath, usePlot } from "./usePlot";
import { useIsBackdrop } from "@/app/layout/backdrop";
import "./plotPage.css";

/** 헤드 태그는 앞에서부터 이만큼만 (AC0-312) */
const TAGS_SHOWN = 10;

const hideImage = (e: SyntheticEvent<HTMLImageElement>) => {
  e.currentTarget.style.visibility = "hidden";
};

/** F02-04 플롯 프로필. 고정 필드 구역 + 블록 구역(intro·firstScene). 함락 난이도·함락률·댓글·팔로우 비노출. */
export function PlotPage() {
  const { key = "" } = useParams<{ key: string }>(); // slug (예전 UUID 주소도 받아 slug 로 바꾼다, AC0-161)
  const navigate = useNavigate();
  const location = useLocation();
  const plot = usePlot(key);
  useCanonicalPlotPath(key, plot, "/plots");
  const slug = plot.data?.slug;
  const backdrop = useIsBackdrop();
  useEffect(() => {
    if (slug && !backdrop) analytics.track("view_plot_profile", { plot_id: slug });
  }, [slug, backdrop]);
  // 헤더 ⋯ 메뉴(사용자 결정 6, AC0-270): 신고(회원 전용) · 공유
  const isMember = useAuthStore((s) => s.status === "member" && onboardingStep(s.user) === null);
  const sheets = useUiStore((s) => s.sheets);
  const openSheet = useUiStore((s) => s.openSheet);
  const closeSheet = useUiStore((s) => s.closeSheet);
  const requestLogin = useUiStore((s) => s.requestLogin);
  const showSnackbar = useUiStore((s) => s.showSnackbar);
  const [sharing, setSharing] = useState(false);
  // 화면을 떠나면 이 화면의 시트를 닫는다(시트 스택은 전역)
  useEffect(
    () => () => {
      closeSheet("plotMenu");
      closeSheet("report");
    },
    [closeSheet],
  );

  if (plot.isPending) {
    return (
      <main className="page">
        <TopNavigation title="" right={null} onBack={() => navigate(-1)} />
        <div className="page-placeholder" role="status">
          {t("common.loading")}
        </div>
      </main>
    );
  }
  if (plot.isError || !plot.data) {
    return (
      <main className="page">
        <TopNavigation title="" right={null} onBack={() => navigate(-1)} />
        <EmptyState message={t("plot.notFound")} action={{ label: t("common.retry"), onClick: () => void plot.refetch() }} />
      </main>
    );
  }

  const p = plot.data;
  const images = p.character.images.length > 0 ? p.character.images : [p.coverUrl];
  const charSub = [p.character.age !== null ? t("plot.age", { age: p.character.age }) : null, p.character.job].filter(Boolean).join(" · ");

  return (
    <main className="page page--wide plot">
      <TopNavigation tone="transparent" title="" onMenu={() => openSheet("plotMenu")} onBack={() => navigate(-1)} className="plot__nav" />
      {/* PC(large) 상단 흐림 배경(584:12471·12472). 그 밖의 폭에서는 숨김 */}
      <div className="plot__backdrop" aria-hidden>
        <img src={p.coverUrl} alt="" onError={hideImage} />
      </div>
      {/* 펼침에서는 3:4 원본을 가운데 두고 남는 자리에 같은 이미지를 흐리게 깐다(402:2490). 폰은 흐림 배경 없이 전폭 */}
      <div className="plot__cover">
        <img src={p.coverUrl} alt="" aria-hidden className="plot__cover-bg" onError={hideImage} />
        <img src={p.coverUrl} alt="" className="plot__cover-img" onError={hideImage} />
      </div>

      <div className="plot__body">
        <section className="plot__head">
          <h1 className="plot__name">{p.name}</h1>
          <p className="plot__charline">
            <span className="plot__charline-name">{p.character.name}</span>
            {charSub && <span className="plot__charline-meta">{charSub}</span>}
          </p>
          <p className="plot__tagline">{p.tagline}</p>
          {p.tags.length > 0 && (
            <div className="plot__tags">
              {p.tags.slice(0, TAGS_SHOWN).map((tag) => (
                <StaticChip key={tag} size="small">
                  #{tag}
                </StaticChip>
              ))}
            </div>
          )}
        </section>

        {/* 시안 순서: 소개 → 캐릭터 → 인트로 (F02-04 body) */}
        {p.intro.length > 0 && (
          <section className="plot__section plot__section--intro">
            <h2 className="plot__section-title">{t("plot.intro")}</h2>
            <BlockRenderer blocks={p.intro} speaker={{ name: p.character.name, avatarUrl: images[0] }} />
          </section>
        )}

        <section className="plot__section plot__character" aria-label={t("plot.character")}>
          <h2 className="plot__section-title">{t("plot.character")}</h2>
          <CharacterCarousel images={images} name={p.character.name} />
          <div className="plot__character-info">
            <p className="plot__character-meta">
              <span className="plot__character-name">{p.character.name}</span>
              {charSub && <span className="plot__character-sub">{charSub}</span>}
            </p>
            {p.character.description && (
              <div className="plot__character-desc">
                <MarkdownBody text={p.character.description} plain />
              </div>
            )}
          </div>
        </section>

        {p.firstScene.length > 0 && (
          <section className="plot__section">
            <h2 className="plot__section-title">{t("plot.firstScene")}</h2>
            <BlockRenderer blocks={p.firstScene} speaker={{ name: p.character.name, avatarUrl: images[0] }} />
          </section>
        )}

        {/* 크리에이터 행(584:12544): 아바타 36 + 이름 / 대화 수. 팔로우·댓글 수는 1차 비노출 */}
        <section className="plot__creator">
          {p.creator.photoUrl ? (
            <img className="plot__creator-avatar" src={p.creator.photoUrl} alt="" />
          ) : (
            <span className="plot__creator-avatar plot__creator-avatar--empty" data-testid="creator-avatar-placeholder" aria-hidden>
              <IconPersonFill size={20} />
            </span>
          )}
          <div className="plot__creator-text">
            <span className="plot__creator-name">{p.creator.name}</span>
            <span className="plot__conversations">{t("plot.conversations", { count: p.totalMessageCount })}</span>
          </div>
        </section>

        {/* 의견 배너(F02-04): 본문 맨 아래, 대화 시작하기 위 */}
        <FeedbackEntry from="plot" className="plot__feedback" />
      </div>

      <BottomCTA
        className="plot__cta"
        primary={{
          // inactive(소유자 탈퇴, account-deletion 정책 4·5): 기존 대화는 채팅 목록·딥링크로 계속 열리지만 여기서 새로 시작은 막는다(서버도 403 PLOT_INACTIVE)
          children: p.status === "inactive" ? t("plot.inactive") : t("plot.cta"),
          disabled: p.status === "inactive",
          onClick: () => {
            // 채팅방 → 플롯 보기 → 대화하기면 쌓지 않고 그 채팅방으로 돌아간다
            if ((location.state as { fromChat?: boolean } | null)?.fromChat) navigate(-1);
            else navigate(`/chat/${encodeURIComponent(p.slug)}`);
          },
        }}
      />
      <PlotMenuSheet
        open={sheets.includes("plotMenu")}
        onReport={() => {
          closeSheet("plotMenu");
          if (!isMember) {
            requestLogin({ returnTo: `/plots/${encodeURIComponent(p.slug)}`, plot: { plotName: p.name, coverUrl: p.coverUrl, tagline: p.tagline } });
            void navigate("/login");
            return;
          }
          openSheet("report");
        }}
        onShare={() => {
          closeSheet("plotMenu");
          if (sharing) return;
          setSharing(true);
          platform.device
            .share({ title: p.name, url: publicWebUrl(`/plots/${encodeURIComponent(p.slug)}`) })
            .then((r) => r === "copied" && showSnackbar(t("plot.share.copied")))
            .catch(() => showSnackbar(t("plot.share.failed")))
            .finally(() => setSharing(false));
        }}
        onClose={() => closeSheet("plotMenu")}
      />
      <ReportScreen
        open={sheets.includes("report")}
        target={{ kind: "plot", plotKey: p.slug, label: t("report.target.plot", { name: p.name }) }}
        onClose={() => closeSheet("report")}
      />
    </main>
  );
}
